import { useCallback, useEffect, useId, useState } from "react";
import type { FormEvent, MouseEvent } from "react";
import { PageHeader } from "@/components/PageHeader";
import { StateMessage } from "@/components/StateMessage";
import { demoApi, problemMessage } from "@/api/client";
import type { Account, GlDetailRow } from "@/api/types";
import { formatQuantity } from "@/lib/registerQuantity";
import "./ReportsGlDetail.css";

type AccountsStatus = "loading" | "ready" | "error";
type ReportStatus = "idle" | "loading" | "ready" | "error";

/**
 * General ledger detail by account — Reports' line-level ledger for ONE account, ef-054-2,
 * ADR 0009 §6 ("Reports is one picker" — a report renders inside the template at
 * `/reports/{slug}`, no area tab strip of its own; "All reports" below is the only way
 * back).
 *
 * <p>
 * ACCOUNT PICKER, ANY KIND — `GET /accounts` with no kind filter (D6): unlike
 * `Books.tsx`'s own "Register account" select (Asset-only), a GL detail listing is for
 * ANY account. Nothing is fetched until an account is chosen — same "no guessed window"
 * discipline `ReportsCashFlow.tsx`'s own remarks give, restated here for the account
 * dimension: a ledger read against an account nobody picked is a listing nobody asked
 * for.
 * </p>
 *
 * <p>
 * CURSOR PAGINATION, NOT OFFSET — "Load more" mirrors `Books.tsx`'s own register list
 * (`useRegister.ts`'s own shape, inlined here rather than a shared hook: this job's own
 * file scope is this page and its CSS only). See `GlDetailRow`'s own remarks in
 * `types.ts` for why an offset page cannot keep a running balance stable.
 * </p>
 *
 * <p>
 * NO $ MONEY FACE ON AMOUNT/RUNNING BALANCE — a general ledger detail listing is a plain
 * accounting ledger (Debit/Credit already names the direction), matching the CSV export's
 * own two-decimal, no-symbol convention (`GlDetailCsv`) rather than `formatMoney`'s
 * currency face reserved for whole-figure KPIs/statements elsewhere in this app.
 * </p>
 */
export function ReportsGlDetail() {
  const accountFieldId = useId();
  const fromId = useId();
  const toId = useId();

  const [accounts, setAccounts] = useState<Account[]>([]);
  const [accountsStatus, setAccountsStatus] = useState<AccountsStatus>("loading");
  const [accountsError, setAccountsError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    (async () => {
      setAccountsStatus("loading");
      try {
        const result = await demoApi.getAccounts(controller.signal);
        setAccounts(result);
        setAccountsStatus("ready");
      } catch (err) {
        if (err instanceof DOMException && err.name === "AbortError") return;
        setAccountsStatus("error");
        setAccountsError(problemMessage(err, "Could not load accounts."));
      }
    })();
    return () => controller.abort();
  }, []);

  const [accountId, setAccountId] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const rangeIsBackwards = from !== "" && to !== "" && to < from;

  const [accountMeta, setAccountMeta] = useState<{ number: string; name: string } | null>(null);
  const [entries, setEntries] = useState<GlDetailRow[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [status, setStatus] = useState<ReportStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  const loadFirstPage = useCallback(
    async (id: string, window: { from?: string; to?: string }, signal?: AbortSignal): Promise<void> => {
      setStatus("loading");
      setError(null);
      try {
        const page = await demoApi.getGlDetailReport(
          { accountId: id, from: window.from || undefined, to: window.to || undefined },
          signal,
        );
        if (signal?.aborted) return;
        setAccountMeta({ number: page.accountNumber, name: page.accountName });
        setEntries(page.entries);
        setCursor(page.nextCursor);
        setHasMore(page.nextCursor !== null);
        setStatus("ready");
      } catch (err) {
        if (signal?.aborted || (err instanceof DOMException && err.name === "AbortError")) return;
        // The previous account's rows are cleared, not kept — a stale ledger for a
        // different account is the one outcome that reads as an answer to this one.
        setAccountMeta(null);
        setEntries([]);
        setCursor(null);
        setHasMore(false);
        setStatus("error");
        setError(problemMessage(err, "Could not load the general ledger detail."));
      }
    },
    [],
  );

  useEffect(() => {
    if (!accountId) {
      setAccountMeta(null);
      setEntries([]);
      setCursor(null);
      setHasMore(false);
      setStatus("idle");
      setError(null);
      return;
    }
    const controller = new AbortController();
    void loadFirstPage(accountId, { from, to }, controller.signal);
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `from`/`to` are applied via
    // the form's own submit below, not on every keystroke; only accountId re-triggers here.
  }, [accountId, loadFirstPage]);

  async function handleApplyWindow(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (!accountId || rangeIsBackwards) return;
    await loadFirstPage(accountId, { from, to });
  }

  async function loadMore(): Promise<void> {
    if (!accountId || !cursor) return;
    try {
      const page = await demoApi.getGlDetailReport({
        accountId,
        from: from || undefined,
        to: to || undefined,
        cursor,
      });
      setEntries((prev) => [...prev, ...page.entries]);
      setCursor(page.nextCursor);
      setHasMore(page.nextCursor !== null);
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") return;
      setError(problemMessage(err, "Could not load more of the ledger."));
    }
  }

  /**
   * The export is its own request, fired on the click — not fetched alongside the report,
   * same reasoning `ReportsIncomeStatement.tsx`'s own identical download gives.
   */
  const [csvState, setCsvState] = useState<
    { kind: "idle" } | { kind: "busy" } | { kind: "done"; rows: number } | { kind: "error"; message: string }
  >({ kind: "idle" });

  async function downloadCsv(): Promise<void> {
    if (!accountId) return;
    setCsvState({ kind: "busy" });
    try {
      const csv = await demoApi.getGlDetailCsv({ accountId, from: from || undefined, to: to || undefined });

      // The bytes are the server writer's output verbatim — same "add a blob and a
      // filename and nothing else" discipline `ReportsIncomeStatement.tsx`'s own download
      // follows.
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `easyfarm-gl-detail-${accountMeta?.number ?? accountId}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);

      const rows = Math.max(csv.trim().split("\n").length - 1, 0);
      setCsvState({ kind: "done", rows });
    } catch (err) {
      setCsvState({
        kind: "error",
        message: problemMessage(err, "Could not build the general ledger detail file."),
      });
    }
  }

  function handleAllReportsClick(event: MouseEvent<HTMLAnchorElement>): void {
    event.preventDefault();
    // Self-contained, same device every other report screen's own handler already uses
    // rather than threading a `navigate` prop through every screen this job touches:
    // pushState doesn't fire popstate on its own, so App.tsx's existing listener is told
    // directly.
    window.history.pushState({}, "", "/reports");
    window.dispatchEvent(new PopStateEvent("popstate"));
  }

  const isEmpty = status === "ready" && entries.length === 0;
  const showQuantity = entries.some((entry) => entry.quantity !== null);

  return (
    <main className="gl-detail">
      <PageHeader
        title="General ledger detail by account"
        subtitle="Every posted line for one account, in order, with a running balance"
      />

      <a href="/reports" className="gl-detail__all-reports" onClick={handleAllReportsClick}>
        ← All reports
      </a>

      <section className="gl-detail__panel" aria-labelledby="gl-detail-heading" data-state={status}>
        <h2 id="gl-detail-heading" className="gl-detail__heading">
          Ledger
        </h2>

        <div className="gl-detail__account-picker">
          <label htmlFor={accountFieldId}>Account</label>
          <select
            id={accountFieldId}
            value={accountId}
            onChange={(event) => setAccountId(event.target.value)}
            disabled={accountsStatus === "loading" || accounts.length === 0}
          >
            <option value="">Choose an account</option>
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>
                {a.number} — {a.name}
              </option>
            ))}
          </select>
        </div>

        {accountsStatus === "error" && (
          <StateMessage tone="error" title="Could not load accounts." detail={accountsError ?? undefined} />
        )}

        <form className="gl-detail__form" onSubmit={(event) => void handleApplyWindow(event)}>
          <div className="gl-detail__field">
            <label htmlFor={fromId}>From</label>
            <input id={fromId} type="date" value={from} onChange={(event) => setFrom(event.target.value)} />
          </div>
          <div className="gl-detail__field">
            <label htmlFor={toId}>To</label>
            <input
              id={toId}
              type="date"
              value={to}
              aria-invalid={rangeIsBackwards || undefined}
              onChange={(event) => setTo(event.target.value)}
            />
          </div>
          <button
            type="submit"
            className="btn btn--primary gl-detail__apply"
            disabled={!accountId || rangeIsBackwards || status === "loading"}
          >
            {status === "loading" ? "Loading…" : "Apply"}
          </button>
        </form>

        {rangeIsBackwards && (
          <p className="gl-detail__field-error" role="alert">
            The end of the period cannot be before its start.
          </p>
        )}

        {status === "loading" && <StateMessage tone="loading" title="Loading the ledger…" />}

        {status === "error" && (
          <StateMessage tone="error" title="Could not load the general ledger detail." detail={error ?? undefined} />
        )}

        {status === "idle" && (
          <StateMessage tone="empty" title="Choose an account above to see its ledger." />
        )}

        {isEmpty && (
          <StateMessage
            tone="empty"
            title="Nothing posted to this account yet."
            detail="Entries posted to this account will show up here."
          />
        )}

        {status === "ready" && entries.length > 0 && (
          <>
            <div className="gl-detail__table-wrap" tabIndex={0} role="group" aria-labelledby="gl-detail-heading">
              <table className="gl-detail__table ruled-rows">
                <caption className="gl-detail__caption">
                  {entries.length} {entries.length === 1 ? "line" : "lines"}
                  {accountMeta ? ` — ${accountMeta.number} ${accountMeta.name}` : ""}
                </caption>
                <thead>
                  <tr>
                    <th scope="col">Date</th>
                    <th scope="col">Side</th>
                    <th scope="col" className="gl-detail__num">Amount</th>
                    <th scope="col" className="gl-detail__num">Running Balance</th>
                    <th scope="col">Vendor</th>
                    {showQuantity && <th scope="col">Qty</th>}
                    <th scope="col">Memo</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((entry) => (
                    <tr key={entry.id}>
                      <td>{entry.occurredOn}</td>
                      <td>{entry.side}</td>
                      <td className="gl-detail__num gl-detail__amount">{entry.amount.toFixed(2)}</td>
                      <td className="gl-detail__num gl-detail__amount">{entry.runningBalance.toFixed(2)}</td>
                      <td>{entry.vendorName ?? "—"}</td>
                      {showQuantity && <td>{entry.quantity === null ? "—" : formatQuantity(entry.quantity, entry.unit ?? "")}</td>}
                      <td>{entry.memo ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {hasMore && (
              <button type="button" className="btn btn--ghost gl-detail__load-more" onClick={() => void loadMore()}>
                Load more
              </button>
            )}

            <div className="gl-detail__export">
              <h3 className="gl-detail__export-title">General ledger detail for your records</h3>
              <p className="gl-detail__export-copy">The whole window above, as a CSV a spreadsheet can open.</p>
              <button
                type="button"
                className="btn btn--secondary gl-detail__export-btn"
                disabled={csvState.kind === "busy"}
                onClick={() => void downloadCsv()}
              >
                {csvState.kind === "busy" ? "Building the file…" : "Download CSV"}
              </button>

              {csvState.kind === "done" && (
                <p className="gl-detail__export-result" role="status">
                  {csvState.rows} {csvState.rows === 1 ? "line" : "lines"} saved.
                </p>
              )}

              {csvState.kind === "error" && (
                <p className="gl-detail__export-error" role="alert">
                  {csvState.message} Nothing was downloaded.
                </p>
              )}
            </div>
          </>
        )}
      </section>
    </main>
  );
}
